import { useMutation } from '@apollo/client/react';
import { Alert, Stack, Text, TextLink, useToast } from 'braid-design-system';
import { createContext, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router';

import {
  NewWebhookForm,
  NewWebhookFormProvider,
} from './components/NewWebhookForm/NewWebhookForm';
import { CREATE_WEBHOOK } from './queries';

import { NoPermissionAlert } from '#src/components/NoPermissionAlert/NoPermissionAlert.tsx';
import { usePermissions } from '#src/hooks/auth.tsx';
import type {
  CreateNewWebhookMutation,
  CreateNewWebhookMutationVariables,
} from '#src/types/generated/graphql.ts';

type UseMutationResult = ReturnType<
  typeof useMutation<
    CreateNewWebhookMutation,
    CreateNewWebhookMutationVariables
  >
>;

const Context = createContext<{
  create: UseMutationResult[0];
  createResult: UseMutationResult[1];
} | null>(null);

export const NewSubscriptionPanelProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const showToast = useToast();
  const navigate = useNavigate();

  const [create, createResult] = useMutation<
    CreateNewWebhookMutation,
    CreateNewWebhookMutationVariables
  >(CREATE_WEBHOOK, {
    update: (cache, { data }) => {
      if (
        data?.createWebhookSubscription?.__typename !==
        'CreateWebhookSubscriptionPayload_Success'
      ) {
        return;
      }

      cache.evict({ fieldName: 'webhookSubscriptions' });
    },
  });

  useEffect(() => {
    if (
      !createResult.called ||
      createResult.loading ||
      createResult.error ||
      !createResult.data
    ) {
      return;
    }

    const { createWebhookSubscription } = createResult.data;

    if (
      createWebhookSubscription.__typename ===
      'CreateWebhookSubscriptionPayload_Success'
    ) {
      showToast({
        tone: 'positive',
        message: 'Subscription created successfully',
      });
      navigate(
        `/manage/webhooks/${encodeURIComponent(
          createWebhookSubscription.webhookSubscription.id.value,
        )}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createResult]);

  return (
    <Context.Provider value={{ create, createResult }}>
      <NewWebhookFormProvider>{children}</NewWebhookFormProvider>
    </Context.Provider>
  );
};

export const NewSubscriptionPanel = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      'NewSubscriptionPanel must be used within a NewSubscriptionPanelProvider',
    );
  }

  const { permissions } = usePermissions();
  const { create, createResult } = context;

  const conflictId =
    createResult.data?.createWebhookSubscription.__typename ===
    'CreateWebhookSubscriptionPayload_Conflict'
      ? createResult.data?.createWebhookSubscription
          .conflictingWebhookSubscription.id.value
      : undefined;

  return (
    <Stack space="medium">
      {permissions.includes('mutate:webhooks') ? (
        <NewWebhookForm
          loading={createResult.loading}
          onSubmit={(variables) => create({ variables })}
        />
      ) : (
        <NoPermissionAlert subject="create subscriptions" />
      )}

      {createResult.error && (
        <Alert tone="critical">
          <Stack space="small">
            <Text>There was a problem creating the webhook</Text>
            <Text size="small">{createResult.error.message}</Text>
          </Stack>
        </Alert>
      )}

      {conflictId ? (
        <Alert tone="caution">
          <Stack space="small">
            <Text>A subscription with these details already exists.</Text>
            <Text size="small">
              <TextLink
                href={`/manage/webhooks/${encodeURIComponent(conflictId)}`}
              >
                {conflictId}
              </TextLink>
            </Text>
          </Stack>
        </Alert>
      ) : null}
    </Stack>
  );
};
